/**
 * RPR-6094 — Chunked S3 multipart uploader for Mux-backed survey videos.
 *
 * Independent of `recording_upload.js` (FusionRecording uploader). Videos
 * warrant a different chunk size (16 MB vs 10 MB), parallel part uploads
 * (4-at-a-time vs serial), and page-refresh resume via `sessionStorage`.
 *
 * Public API (matches docs/plans/RPR-6094-video-support.md, Task 8.2):
 *   const uploader = videoUploaderFactory.create({
 *     file,
 *     onProgress: fraction => ...,   // fraction in [0, 1]
 *     onComplete: ({document_id}) => ...,
 *     onError:    err => ...
 *   });
 *   uploader.start();
 *   uploader.pause();
 *   uploader.resume();
 *
 * We expose a factory that returns a `VideoUploader` class so the existing
 * Angular DI wiring (apiHelper, HttpAuth, $http, CONSTANTS) is honoured —
 * the underlying $http POSTs to /api/authoring/video/* automatically pick
 * up the CSRF token via the `csrfInterceptor` in app_common.js.
 */
app.factory('videoUploaderFactory', [
  '$http', '$q', '$timeout', 'HttpAuth', 'apiHelper', 'CONSTANTS',
  function ($http, $q, $timeout, HttpAuth, apiHelper, CONSTANTS) {
    const API_URL = apiHelper.getApiUrl();
    const CHUNK_BYTES = CONSTANTS.VIDEO_CHUNK_BYTES;
    const MAX_CONCURRENT = CONSTANTS.VIDEO_MAX_CONCURRENT_PARTS;
    const MAX_BYTES = CONSTANTS.VIDEO_MAX_BYTES;
    const ACCEPTED_MIMES = CONSTANTS.VIDEO_ACCEPTED_MIMES;
    const ACCEPTED_EXTS = CONSTANTS.VIDEO_ACCEPTED_EXTS;
    // Up to 3 attempts per part (initial + 2 backoff retries). ``.length`` is the
    // attempt cap; only delays [0]=1s and [1]=2s are actually waited (the 3rd
    // attempt rejects on failure), so the trailing 4000 only pads the cap to 3 --
    // keep all three entries or the retry count changes.
    const RETRY_BACKOFF_MS = [1000, 2000, 4000];
    // RPR-6747: bound every upload-phase request so a stalled connection can no
    // longer wedge the upload forever (it used to freeze the bar at 0/75/90/99%).
    const PART_STALL_MS = CONSTANTS.VIDEO_PART_STALL_MS;
    const INITIATE_TIMEOUT_MS = CONSTANTS.VIDEO_INITIATE_TIMEOUT_MS;
    const COMPLETE_TIMEOUT_MS = CONSTANTS.VIDEO_COMPLETE_TIMEOUT_MS;

    const SESSION_KEY_PREFIX = 'rpr-video-upload-';

    function validateFile(file) {
      const errors = [];
      if (!file) {
        errors.push('No file selected');
        return errors;
      }
      if (file.size > MAX_BYTES) {
        errors.push('File size must be 2 GB or less');
      }
      const lowerName = (file.name || '').toLowerCase();
      const ext = lowerName.includes('.') ? '.' + lowerName.split('.').pop() : '';
      const mimeOk = ACCEPTED_MIMES.includes(file.type);
      const extOk = ACCEPTED_EXTS.includes(ext);
      if (!mimeOk && !extOk) {
        errors.push('Supported formats: MP4, MOV, WebM');
      }
      return errors;
    }

    // Upload a single chunk to a presigned S3 URL. Returns a $q promise
    // resolving to the unquoted ETag. We bypass `HttpAuth` because S3 must
    // not receive our session cookie / CSRF header (it would 400).
    //
    // RPR-6747: guard against a stalled S3 PUT. `$http`'s `timeout` accepts a
    // promise that, when resolved, aborts the request; we resolve it when the
    // upload makes no progress for PART_STALL_MS. We reset that stall timer on
    // every `xhr.upload` progress event (via `uploadEventHandlers`), so a slow-
    // but-progressing upload on a poor connection is never killed -- only a dead
    // connection is. An aborted part rejects here, and `putPartWithRetry` retries
    // it; if every retry stalls the upload surfaces an error instead of the old
    // behaviour of hanging on the upload screen indefinitely.
    function putPart(presignedUrl, blob) {
      const abort = $q.defer();
      let stallTimer = null;
      const cancelStallTimer = function () {
        if (stallTimer) {
          $timeout.cancel(stallTimer);
          stallTimer = null;
        }
      };
      const armStallTimer = function () {
        cancelStallTimer();
        stallTimer = $timeout(function () { abort.resolve(); }, PART_STALL_MS);
      };
      // Arm before sending so a connection that never makes progress at all
      // (e.g. it never opens) is still aborted rather than hanging forever.
      armStallTimer();
      return $http.put(presignedUrl, blob, {
        headers: { 'Content-Type': 'application/octet-stream' },
        withCredentials: false,
        transformRequest: angular.identity,
        timeout: abort.promise,
        uploadEventHandlers: { progress: armStallTimer }
      }).then(function (response) {
        cancelStallTimer();
        const etag = (response.headers('ETag') || '').replaceAll('"', '');
        if (!etag) {
          return $q.reject(new Error('S3 did not return ETag'));
        }
        return etag;
      }, function (err) {
        // Real error or a stall-triggered abort -- either way let the retry
        // wrapper decide. Surface a clearer reason for an aborted (stalled) PUT.
        cancelStallTimer();
        if (err?.status === -1) {
          return $q.reject(new Error('Upload stalled (no progress); retrying part.'));
        }
        return $q.reject(err);
      });
    }

    // Retry wrapper: up to 3 attempts per part with exponential backoff, then
    // propagate the failure to the caller.
    function putPartWithRetry(presignedUrl, blob) {
      let attempt = 0;
      function tryOnce() {
        return putPart(presignedUrl, blob).catch(function (err) {
          attempt += 1;
          if (attempt >= RETRY_BACKOFF_MS.length) {
            // All attempts exhausted -- propagate the failure to the caller.
            return $q.reject(err);
          }
          return $q(function (resolve) {
            setTimeout(resolve, RETRY_BACKOFF_MS[attempt - 1]);
          }).then(tryOnce);
        });
      }
      return tryOnce();
    }

    // Compute a fingerprint for duplicate detection: SHA-256 over the first
    // 1 MB + last 1 MB of the file + the file size (as ASCII bytes). Catches
    // typical re-encodes because video container headers + trailers are
    // highly distinctive. For files smaller than 1 MB the head and tail
    // ranges overlap, which is fine -- we only WARN on a match server-side.
    // Sampling (not a full-file hash) is intentional: hashing a 2 GB upload
    // would allocate a multi-GB ArrayBuffer and block the main thread for
    // seconds, whereas sampling keeps fingerprinting under ~50 ms.
    async function computeSampledHash(file) {
      const SAMPLE_BYTES = 1024 * 1024;
      const head = await file.slice(0, SAMPLE_BYTES).arrayBuffer();
      const tailStart = Math.max(0, file.size - SAMPLE_BYTES);
      const tail = await file.slice(tailStart).arrayBuffer();
      const sizeBytes = new TextEncoder().encode(String(file.size));
      const combined = new Uint8Array(head.byteLength + tail.byteLength + sizeBytes.byteLength);
      combined.set(new Uint8Array(head), 0);
      combined.set(new Uint8Array(tail), head.byteLength);
      combined.set(sizeBytes, head.byteLength + tail.byteLength);
      const digest = await crypto.subtle.digest('SHA-256', combined);
      return Array.from(new Uint8Array(digest))
        .map(function (b) { return b.toString(16).padStart(2, '0'); })
        .join('');
    }

    function checkDuplicate(opts) {
      const surveyId = opts.surveyId;
      const contentSampleHash = opts.contentSampleHash;
      const filename = opts.filename;
      const url = `${API_URL}/api/authoring/video/check-duplicate/`;
      const payload = {
        survey: surveyId,
        content_sample_hash: contentSampleHash,
        filename: filename
      };
      // 2 second timeout — dedupe is a "nice to have"; never block the upload.
      return HttpAuth.post(url, payload, { timeout: 2000 }).then(function (response) {
        return response?.data?.duplicates || [];
      }, function () {
        // Network error / 5xx / timeout: skip dedupe quietly.
        return [];
      });
    }

    class VideoUploader {
      constructor(opts) {
        const options = opts || {};
        this.file = options.file;
        // surveyId is optional; when passed, the backend tags the new
        // Document with this survey so it shows up in the legacy admin's
        // per-survey document-picker filter (mirrors the existing PDF
        // upload behaviour).
        this.surveyId = options.surveyId || null;
        // ownerId is optional; when passed, the backend sets the new
        // Document's owner to this user. Mirrors the legacy PDF
        // upload's `owner_id` form field -- the EM uploads on behalf
        // of the survey's client user, so the doc needs that user's
        // id to clear the controller's owner_id-based filter.
        this.ownerId = options.ownerId || null;
        this.onProgress = options.onProgress || function () {};
        this.onComplete = options.onComplete || function () {};
        this.onError = options.onError || function () {};
        this.onDuplicate = options.onDuplicate || null;
        this.contentSampleHash = null;
        this.documentId = null;
        // RPR-6711: id of the existing video being replaced in place. When set,
        // the uploader runs in REPLACE mode -- it presigns + finalises against
        // that existing document (initiate-replace -> /{id}/replace/) instead of
        // creating a new row. Set on a filename-collision confirm, or passed in
        // by a caller that explicitly chose "replace this clip". The existing
        // video keeps playing until the replacement's new Mux asset is ready.
        this.replaceTargetId = options.replaceTargetId || null;
        this.s3UploadId = null;
        this.presignedUrls = [];
        this.completedParts = []; // each entry has an ETag and a PartNumber
        this.cancelled = false;
      }

      start() {
        const errs = validateFile(this.file);
        if (errs.length > 0) {
          this.onError({ errors: errs });
          return $q.reject({ errors: errs });
        }
        this.cancelled = false;
        const self = this;

        return $q.when()
          .then(function () { return self._computeHashSafe(); })
          .then(function () {
            if (self.cancelled) return false;
            return self._checkDuplicateGate();
          })
          .then(function (proceed) {
            if (!proceed) { return null; }
            return $q.when()
              .then(function () { return self._ensureSession(); })
              .then(function () { return self._uploadParts(); })
              .then(function () {
                if (self.cancelled) return null;
                return self._complete();
              })
              .then(function (result) {
                if (self.cancelled) return null;
                self._clearSession();
                self.onComplete({
                  document_id: self.documentId,
                  // RPR-6711: true when this finalised an in-place REPLACE (same
                  // document id) rather than a brand-new upload. The caller uses
                  // it to track the replacement via replace_status, not status.
                  is_replace: !!self.replaceTargetId,
                  response: result
                });
                return result;
              });
          })
          .catch(function (err) {
            // Filename collision (409) bubbled up from _initiate. The frontend
            // sends the new file's hash + filename; the backend may have noticed
            // an EXISTING video with the same filename in the same survey and
            // returned 409 with that doc's id. Re-invoke onDuplicate with the
            // filename match so the EM can confirm overwrite or cancel.
            if (err?.status === 409 && err?.data?.existing_document_id) {
              return self._handleFilenameCollision(err.data.existing_document_id);
            }
            self.onError(err);
            return $q.reject(err);
          });
      }

      pause() {
        this.cancelled = true;
      }

      resume() {
        // start() picks up the in-flight session from sessionStorage.
        return this.start();
      }

      // RPR-6745: cancel an in-flight upload and retire its placeholder.
      // `pause()` only stops launching new parts; the backing Document created
      // by `_initiate()` would otherwise linger as a "Video not available"
      // record and block re-uploading the same file. This stops the upload AND
      // asks the backend to soft-delete the placeholder + abort the S3
      // multipart. Best-effort and safe to call repeatedly: with no document_id
      // (cancelled before `_initiate`) there is nothing to clean up, and the
      // backend ignores a request for an already-finished upload.
      abort() {
        this.cancelled = true;
        const documentId = this.documentId;
        const s3UploadId = this.s3UploadId;
        this._clearSession();
        if (!documentId) { return $q.when(); }
        const url = `${API_URL}/api/authoring/video/abort-upload/`;
        return HttpAuth.post(
          url,
          { document_id: documentId, s3_upload_id: s3UploadId || '' },
          { timeout: 4000 }
        ).catch(function () {
          // Best-effort: the backend stale-upload sweep is the backstop.
        });
      }

      _computeHashSafe() {
        if (!globalThis.crypto?.subtle) {
          return $q.when();
        }
        return $q.when(computeSampledHash(this.file))
          .then((h) => { this.contentSampleHash = h; })
          .catch(function () { /* leave hash null — degrade gracefully */ });
      }

      // Builds the handlers object handed to the host's onDuplicate callback for
      // the content-duplicate gate. Extracted from `_checkDuplicateGate` so the
      // proceed/cancel closures aren't nested past Sonar's function depth
      // limit (S2004); behaviour is unchanged.
      _contentDuplicateHandlers(duplicates, resolve) {
        return {
          duplicates: duplicates,
          proceed: () => {
            // RPR-6711: "replace" targets the filename-matching clip; setting
            // replaceTargetId before resolving makes the in-progress start()
            // run in replace mode. RPR-6758: a doc matching content AND name
            // arrives as 'both' and is a replace target too. A content-only
            // match has no filename target, so it proceeds as a new upload
            // ("upload anyway").
            const filenameMatch = duplicates.find(function (d) {
              return d.match_type === 'filename' || d.match_type === 'both';
            });
            if (filenameMatch) { this.replaceTargetId = filenameMatch.document_id; }
            resolve(true);
          },
          cancel: function () { resolve(false); }
        };
      }

      // Resolves with `true` to proceed with upload, `false` to abort (cancel).
      // Pauses on the onDuplicate callback when duplicates are found.
      _checkDuplicateGate() {
        const self = this;
        if (self.cancelled) { return $q.when(false); }
        if (!self.onDuplicate || !self.surveyId || self.replaceTargetId) {
          return $q.when(true);
        }
        return $q.when(checkDuplicate({
          surveyId: self.surveyId,
          contentSampleHash: self.contentSampleHash || '',
          filename: self.file.name
        })).then(function (duplicates) {
          if (!duplicates.length) { return true; }
          return $q(function (resolve, reject) {
            try {
              self.onDuplicate(self._contentDuplicateHandlers(duplicates, resolve));
            } catch (e) {
              reject(e);
            }
          });
        });
      }

      _handleFilenameCollision(existingDocumentId) {
        // Called when the server returned 409 because the filename collides
        // with an existing non-soft-deleted video in this survey. Trigger the
        // onDuplicate filename-modal path, then re-run start() in REPLACE mode
        // against the colliding document if confirmed (RPR-6711).
        if (!this.onDuplicate) {
          this.onError({ data: { detail: 'Filename already in use.' } });
          return $q.reject({ status: 409, existingDocumentId: existingDocumentId });
        }
        return $q((resolve, reject) => {
          // Mirror _checkDuplicateGate's try/reject: $q executors don't catch
          // throws, so a throwing host handler would otherwise strand the row
          // at "Uploading" with the promise forever pending (RPR-6758).
          try {
            this.onDuplicate({
              duplicates: [{
                document_id: existingDocumentId,
                match_type: 'filename',
                name: this.file.name
              }],
              proceed: () => {
                // RPR-6711: replace the existing clip in place -- re-run in replace
                // mode (initiate-replace -> /{id}/replace/) against the colliding
                // document instead of creating a second row.
                this.replaceTargetId = existingDocumentId;
                this.documentId = null;
                this.s3UploadId = null;
                this.presignedUrls = [];
                this.completedParts = [];
                this._clearSession();
                this.start().then(resolve, reject);
              },
              // resolve(null) means user aborted after a 409. The outer start() promise
              // resolves with null (not the upload result) so callers must treat a null
              // resolution as "no upload happened" -- onComplete is NOT called for null.
              cancel: function () { resolve(null); }
            });
          } catch (e) {
            reject(e);
          }
        });
      }

      _ensureSession() {
        const saved = this._loadSession();
        // RPR-6711: only rehydrate a saved upload when it belongs to the SAME
        // survey AND the SAME replace intent. The session key is file
        // name/size only, so without this a stale session for an identically
        // named/sized file could route /replace/ at the wrong document -- e.g.
        // a leftover replace session restored under a fresh upload, or a
        // session from a different survey.
        const sameContext = saved
          && saved.fileName === this.file.name
          && saved.fileSize === this.file.size
          && saved.surveyId === this.surveyId
          && String(saved.replaceTargetId || '') === String(this.replaceTargetId || '');
        if (sameContext) {
          this.documentId = saved.documentId;
          this.replaceTargetId = saved.replaceTargetId || null;
          this.s3UploadId = saved.s3UploadId;
          this.presignedUrls = saved.presignedUrls;
          this.completedParts = saved.completedParts || [];
          return $q.when();
        }
        return this._initiate();
      }

      _initiate() {
        // RPR-6711: in replace mode, presign a multipart against the EXISTING
        // document (initiate-replace) so no new row is created; otherwise this
        // is a brand-new upload (initiate-upload).
        const url = this.replaceTargetId
          ? `${API_URL}/api/authoring/video/${this.replaceTargetId}/initiate-replace/`
          : `${API_URL}/api/authoring/video/initiate-upload/`;
        const payload = {
          filename: this.file.name,
          size: this.file.size,
          content_type: this.file.type
        };
        if (!this.replaceTargetId) {
          // survey/owner only matter when creating a new Document; a replace
          // keeps the existing row's survey + owner.
          if (this.surveyId) {
            payload.survey = this.surveyId;
          }
          if (this.ownerId) {
            payload.owner_id = this.ownerId;
          }
          if (this.contentSampleHash) {
            payload.content_sample_hash = this.contentSampleHash;
          }
        }
        // RPR-6747: bound the initiate POST so a stalled request can't leave the
        // bar at 0% indefinitely (the force-overwrite re-initiate is the second
        // initiate on the duplicate->Replace path, so it doubles the exposure).
        return HttpAuth.post(url, payload, { timeout: INITIATE_TIMEOUT_MS }).then((response) => {
          const data = response?.data || {};
          this.documentId = data.document_id;
          this.s3UploadId = data.s3_upload_id;
          this.presignedUrls = data.presigned_part_urls || [];
          this.completedParts = [];
          this._saveSession();
        });
      }

      _uploadParts() {
        const self = this;
        const completedNumbers = new Set(this.completedParts.map(function (p) { return p.PartNumber; }));
        const queue = [];
        for (let i = 0; i < this.presignedUrls.length; i++) {
          const partNumber = i + 1;
          if (!completedNumbers.has(partNumber)) {
            queue.push({ partNumber: partNumber, url: this.presignedUrls[i] });
          }
        }

        let cursor = 0;
        const total = this.presignedUrls.length;
        const reportProgress = function () {
          if (total === 0) {
            self.onProgress(1);
          } else {
            self.onProgress(self.completedParts.length / total);
          }
        };
        reportProgress();

        function next() {
          if (self.cancelled) return $q.when();
          if (cursor >= queue.length) return $q.when();
          const part = queue[cursor++];
          const start = (part.partNumber - 1) * CHUNK_BYTES;
          const end = Math.min(start + CHUNK_BYTES, self.file.size);
          const blob = self.file.slice(start, end);
          return putPartWithRetry(part.url, blob).then(function (etag) {
            // RPR-6745: a part request already in flight when abort()/pause()
            // fired must not re-persist a session abort() just cleared, or the
            // dead upload would resurface to reapInterruptedUploads()/resume().
            if (self.cancelled) { self._clearSession(); return null; }
            self.completedParts.push({ ETag: etag, PartNumber: part.partNumber });
            self._saveSession();
            reportProgress();
            return next();
          });
        }

        const workers = [];
        const workerCount = Math.min(MAX_CONCURRENT, queue.length) || 1;
        for (let w = 0; w < workerCount; w++) {
          workers.push(next());
        }
        return $q.all(workers);
      }

      _complete() {
        // S3 requires Parts ordered by PartNumber ascending.
        const parts = this.completedParts.slice().sort(function (a, b) {
          return a.PartNumber - b.PartNumber;
        });
        // RPR-6711: in replace mode, finalise + stage the new asset against the
        // existing document (POST /{id}/replace/) so the old video keeps playing
        // until the replacement is ready; otherwise finalise the new upload.
        if (this.replaceTargetId) {
          const replaceUrl = `${API_URL}/api/authoring/video/${this.documentId}/replace/`;
          const replacePayload = {
            s3_upload_id: this.s3UploadId,
            parts: parts
          };
          if (this.contentSampleHash) {
            replacePayload.content_sample_hash = this.contentSampleHash;
          }
          // RPR-6747: /replace/ runs the same synchronous S3 CompleteMultipartUpload
          // + Mux create_asset in-request as complete-upload, so bound it the same
          // way -- otherwise a stalled finalize freezes the bar near 100% forever.
          return HttpAuth.post(replaceUrl, replacePayload, { timeout: COMPLETE_TIMEOUT_MS }).then(function (response) {
            return response?.data;
          });
        }
        const url = `${API_URL}/api/authoring/video/complete-upload/`;
        // RPR-6747: bound the complete POST. The backend runs S3
        // CompleteMultipartUpload + a synchronous Mux create_asset in-request,
        // so a stalled finalize used to freeze the bar near 100% forever; the
        // timeout surfaces an error (errored row) instead.
        return HttpAuth.post(url, {
          document_id: this.documentId,
          s3_upload_id: this.s3UploadId,
          parts: parts
        }, { timeout: COMPLETE_TIMEOUT_MS }).then(function (response) {
          return response?.data;
        });
      }

      _sessionKey() {
        return SESSION_KEY_PREFIX + this.file.name + '-' + this.file.size;
      }

      _loadSession() {
        try {
          const raw = sessionStorage.getItem(this._sessionKey());
          return raw ? JSON.parse(raw) : null;
        } catch (e) {
          return null;
        }
      }

      _saveSession() {
        try {
          sessionStorage.setItem(this._sessionKey(), JSON.stringify({
            fileName: this.file.name,
            fileSize: this.file.size,
            surveyId: this.surveyId,
            documentId: this.documentId,
            replaceTargetId: this.replaceTargetId,
            s3UploadId: this.s3UploadId,
            presignedUrls: this.presignedUrls,
            completedParts: this.completedParts
          }));
        } catch (e) {
          // Quota exceeded or storage unavailable — non-fatal; the upload
          // can still proceed, just no resume across page refresh.
        }
      }

      _clearSession() {
        try {
          sessionStorage.removeItem(this._sessionKey());
        } catch (e) {
          // ignored
        }
      }
    }

    // RPR-6745: clean up uploads abandoned by a page refresh / tab close.
    // A `File` handle cannot survive a reload, so an upload whose session is
    // still in sessionStorage on the next load is dead and can never resume.
    // Retire its placeholder Document (+ abort the S3 multipart) via the
    // abort-upload endpoint and drop the orphaned session. Best-effort: the
    // backend 6h stale-upload sweep is the backstop. Called once on app
    // bootstrap (appController) when the user is logged in. Returns a promise
    // resolving to the reaped sessions [{fileName}] so the caller can notify.
    function reapInterruptedUploads() {
      const reaped = [];
      const keys = [];
      try {
        for (let i = 0; i < sessionStorage.length; i++) {
          const k = sessionStorage.key(i);
          if (k?.startsWith(SESSION_KEY_PREFIX)) { keys.push(k); }
        }
      } catch (e) {
        return $q.when(reaped); // storage unavailable — nothing to do
      }

      const aborts = [];
      const url = `${API_URL}/api/authoring/video/abort-upload/`;
      keys.forEach(function (k) {
        let session = null;
        try { session = JSON.parse(sessionStorage.getItem(k)); } catch { session = null; }
        // Always drop the dead session: with the File gone it can never resume.
        try { sessionStorage.removeItem(k); } catch { /* ignored */ }
        // Only sessions past _initiate have a backend placeholder to retire.
        if (!session?.documentId) { return; }
        reaped.push({ fileName: session.fileName || '' });
        aborts.push(
          HttpAuth.post(
            url,
            { document_id: session.documentId, s3_upload_id: session.s3UploadId || '' },
            { timeout: 4000 }
          ).catch(function () { /* best-effort: backend stale-upload sweep is the backstop */ })
        );
      });

      return $q.all(aborts).then(function () { return reaped; });
    }

    return {
      // Factory entry point: `videoUploaderFactory.create({...})`.
      create: function (opts) { return new VideoUploader(opts); },
      validateFile: validateFile,
      // RPR-6745: bootstrap cleanup of refresh-abandoned uploads.
      reapInterruptedUploads: reapInterruptedUploads,
      // Exposed for tests / dialog wiring.
      VideoUploader: VideoUploader
    };
  }
]);
